import React from 'react';
import {
  Dimensions, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import MinusInCircle from '../../assets/images/minus-in-circle.svg';
import PlusInCircle from '../../assets/images/plus-in-circle.svg';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';

interface IProps {
  servings: number;
  onChanged(servings: number): void;
  t(key: string): string;
}

const ranges = {
  min: 1,
  max: 9,
};

const { width } = Dimensions.get('window');
const counterWidth = () => {
  if (width < 350) {
    return 30;
  }
  if (width < 376) {
    return 40;
  }
  return 50;
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: defaultScale(20),
    paddingBottom: defaultScale(10),
  },
  infoContainer: {
    flex: 3,
    paddingStart: defaultScale(24),
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  controlContainer: {
    flex: 2,
    marginHorizontal: defaultScale(24),
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  title: {
    ...Theme.textStyle.bodyCopyReg,
    width: '100%', // force subtitle into new line
  },
  subTitle: {
    ...Theme.textStyle.small,
    color: Theme.GREY_MEDIUM,
    width: '100%',
  },
  counter: {
    ...Theme.textStyle.heading2,
    textAlign: 'center',
    minWidth: defaultScale(counterWidth()), // force text width to not have minus jump: enough for padding and text
  },
  controlImage: {
    width: defaultScale(36),
    height: defaultScale(36),
    resizeMode: 'contain',
  },
});

const ServingsCounter: React.FC<IProps> = (props: IProps) => {
  const { servings, onChanged, t } = props;

  const canDecrease = servings > ranges.min;
  const canIncrease = servings < ranges.max;

  const rawStylesForWebCompat = {
    enabledImage: {
      color: Theme.PRIMARY,
    },
    disabledImage: {
      color: Theme.GREY_MEDIUM_LIGHT,
    },
  };

  return (
    <View style={styles.outerContainer}>
      <View style={styles.infoContainer}>
        <Text style={styles.title}>{t('servingsCounterTitle')}</Text>
        <Text style={styles.subTitle}>{t('servingsCounterSubTitle')}</Text>
      </View>

      <View style={styles.controlContainer}>
        <TouchableOpacity disabled={!canDecrease} onPress={() => onChanged(servings - 1)}>
          <MinusInCircle style={{
            // spread and not array for compatability with web
            ...styles.controlImage,
            ...(canDecrease ? rawStylesForWebCompat.enabledImage : rawStylesForWebCompat.disabledImage),
          }}
          />
        </TouchableOpacity>

        <Text style={styles.counter}>{servings}</Text>

        <TouchableOpacity disabled={!canIncrease} onPress={() => onChanged(servings + 1)}>
          <PlusInCircle style={{
            // spread and not array for compatability with web
            ...styles.controlImage,
            ...(canIncrease ? rawStylesForWebCompat.enabledImage : rawStylesForWebCompat.disabledImage),
          }}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default ServingsCounter;
